import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { CopyAll } from '@mui/icons-material';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';

import { CreateSinglePromoClonePayload } from '~/hooks/usePromo';

import { BulkCreatePromosResult } from '~/services/PromoService';

import PromoEmancipateClonedPromosOption from './PromoEmancipateClonedPromosOption';

type PromoCopyButtonProps = {
  promoId: string;
  codeName: string;
  parentCodeName: string | null;
  createSinglePromoClone: (payload: CreateSinglePromoClonePayload) => Promise<BulkCreatePromosResult>;
  isLoading: boolean;
};

function PromoCopyButton({
  promoId,
  codeName,
  createSinglePromoClone,
  isLoading,
  parentCodeName,
}: PromoCopyButtonProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [emancipateChildPromos, setEmancipateChildPromos] = useState(false);
  const [cloning, setCloning] = useState(false);

  const busy = isLoading || cloning;

  const handleClone = async () => {
    setCloning(true);
    try {
      const promo = await createSinglePromoClone({
        id_promo_code: promoId,
        promo_prefix: parentCodeName ?? codeName,
        emancipate_child_promos: emancipateChildPromos || undefined,
      });
      if (promo) {
        await navigator.clipboard.writeText(promo.result.promos[0].code_name);
        enqueueSnackbar(`${promo.result.promos[0].code_name}: Promo code created and copied to clipboard!`, {
          variant: 'success',
        });
        setDialogOpen(false);
      }
    } finally {
      setCloning(false);
    }
  };

  return (
    <>
      <Button variant="contained" onClick={() => setDialogOpen(true)} disabled={busy}>
        {busy ? (
          'Copying...'
        ) : (
          <Typography component="span">
            Clone this Promo <CopyAll />
          </Typography>
        )}
      </Button>
      <Dialog open={dialogOpen} onClose={() => !busy && setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Clone this promo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates one new code from <strong>{codeName}</strong> with a random suffix and copies it to the clipboard.
          </Typography>
          <PromoEmancipateClonedPromosOption
            checked={emancipateChildPromos}
            onChange={setEmancipateChildPromos}
            disabled={busy}
            plural={false}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleClone} disabled={busy}>
            {cloning ? 'Cloning…' : 'Clone'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default PromoCopyButton;
